import CuesheetStore from 'flux/stores/cuesheet/CuesheetStore';

/**
 * Row-order sync helpers shared by GlobalElementStore and
 * GlobalElementModalStore, which both mirror the cuesheet's itemSet row order
 * and must apply local move deltas / defer remote orders identically.
 */

/**
 * Apply a moveRows delta (rowIds → after afterRowId) to a plain rowOrder array
 * IN PLACE, matching how the moveRows endpoint and the cuesheet apply it.
 *
 * Returns true if the delta was applied, or false if it was skipped because the
 * order is stale — a defined afterRowId that isn't in the order means our copy
 * predates a move we haven't seen yet, so we leave it for the next realtime
 * order to replace. An undefined afterRowId means "move to the top".
 */
export const applyRowMoveDelta = (rowOrder, rowIds, afterRowId) => {
  if (afterRowId && rowOrder.indexOf(afterRowId) === -1) return false;

  //Remove the moved rows from their current positions
  rowIds.forEach((rowId) => {
    const rowIdIndex = rowOrder.indexOf(rowId);
    if (rowIdIndex !== -1) rowOrder.splice(rowIdIndex, 1);
  });

  const insertAt = rowOrder.indexOf(afterRowId) + 1;

  //Insert at an advancing offset so multiple rows keep their order (a fixed
  //index would insert them reversed, diverging from the API result)
  rowIds.forEach((rowId, offset) => {
    rowOrder.splice(insertAt + offset, 0, rowId);
  });

  return true;
};

/**
 * Whether an incoming realtime row order for `itemSetId` should be deferred
 * because the cuesheet has local moves pending for that itemSet. Mirrors the
 * cuesheet's own guard (CuesheetRealTimeMixin._updateRowOrder): applying an
 * order computed before those moves landed would revert the optimistic order
 * the GEM stores applied via onRowsMovedLocally, leaving them showing a
 * different, staler order than the cuesheet. Once the moves settle the next
 * realtime order re-syncs the GEM. Scoped by itemSetId so a move pending for
 * another itemSet doesn't suppress unrelated orders.
 */
export const remoteRowOrderDeferred = (itemSetId) =>
  CuesheetStore._hasPendingRowMoves(itemSetId);
